import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { User, Mail, Lock, Eye, EyeOff, ArrowRight, LogIn, UserPlus, ShieldCheck, Zap, RefreshCw } from "lucide-react";
import Brand from "@/components/Brand";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    username: "",
    email: "",
    password: "",
    password_confirm: "",
  });
  const [show1, setShow1] = useState(false);
  const [show2, setShow2] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.password_confirm) {
      toast.error("Şifreler eşleşmiyor");
      return;
    }
    setLoading(true);
    try {
      await register(form);
      toast.success("Hesabınız oluşturuldu — hoş geldiniz!");
      navigate("/panel");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen brand-bg text-white auth-glow-bg pb-40">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/50 border-b border-white/10">
        <div className="max-w-2xl mx-auto flex items-center justify-between px-5 h-16">
          <Link to="/login"><Brand size="sm" /></Link>
          <div className="flex items-center gap-2">
            <Link to="/login" className="w-9 h-9 border border-white/20 rounded-lg flex items-center justify-center text-white/70 hover:text-white hover:border-white/40 transition-colors" title="Giriş" data-testid="header-login">
              <LogIn className="w-4 h-4" />
            </Link>
            <Link to="/register" className="w-9 h-9 rounded-lg flex items-center justify-center btn-brand" title="Kayıt Ol" data-testid="header-register">
              <UserPlus className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-md mx-auto px-5 pt-8">
        <div className="mb-6">
          <div className="inline-flex items-center gap-1.5 border border-[hsl(0,84%,60%)]/40 bg-[hsl(0,84%,60%)]/10 text-[hsl(0,84%,68%)] px-3 py-1 rounded-full text-xs font-bold mb-4">
            <Zap className="w-3 h-3" /> Hemen ücretsiz kayıt olun.
          </div>
          <h1 className="font-display font-black text-3xl mb-2">Aramıza Katılın.</h1>
          <p className="text-white/60 text-sm leading-relaxed">
            Ücretsiz üyelik ile 50+ ülke ve 100+ servis için sanal numaralara anında erişin.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-5 border-t border-white/10 pt-6" data-testid="register-form">
          <Field label="Ad" icon={User}>
            <input data-testid="reg-first-name" value={form.first_name} onChange={set("first_name")} required maxLength={64} placeholder="Adınız" className={INPUT_CLS} />
          </Field>

          <Field label="Soyad" icon={User}>
            <input data-testid="reg-last-name" value={form.last_name} onChange={set("last_name")} required maxLength={64} placeholder="Soyadınız" className={INPUT_CLS} />
          </Field>

          <Field label="Kullanıcı Adı" icon={User} hint="Sadece harf, rakam ve alt çizgi kullanın.">
            <input data-testid="reg-username" value={form.username} onChange={set("username")} required minLength={3} maxLength={32} placeholder="ornek_kullanici" className={INPUT_CLS} />
          </Field>

          <Field label="E-posta Adresi" icon={Mail}>
            <input data-testid="reg-email" type="email" value={form.email} onChange={set("email")} required placeholder="ornek@email.com" className={INPUT_CLS} />
          </Field>

          <Field label="Şifre" icon={Lock} hint="En az 6 karakter.">
            <input data-testid="reg-password" type={show1 ? "text" : "password"} value={form.password} onChange={set("password")} required minLength={6} placeholder="••••••••" className={INPUT_CLS + " pr-11"} />
            <ToggleEye visible={show1} onClick={() => setShow1((v) => !v)} testid="reg-toggle-pw1" />
          </Field>

          <Field label="Şifre Tekrarı" icon={Lock}>
            <input data-testid="reg-password-confirm" type={show2 ? "text" : "password"} value={form.password_confirm} onChange={set("password_confirm")} required minLength={6} placeholder="••••••••" className={INPUT_CLS + " pr-11"} />
            <ToggleEye visible={show2} onClick={() => setShow2((v) => !v)} testid="reg-toggle-pw2" />
          </Field>

          <button
            type="submit"
            disabled={loading}
            data-testid="register-submit-btn"
            className="btn-brand w-full font-bold text-base py-3.5 rounded-2xl inline-flex items-center justify-center gap-2 mt-2"
          >
            {loading ? "Kaydediliyor…" : (<>Ücretsiz Kayıt Ol <ArrowRight className="w-4 h-4" /></>)}
          </button>

          <div className="text-sm text-white/60 text-center pt-2">
            Zaten bir hesabınız var mı?{" "}
            <Link to="/login" className="text-white font-bold hover:text-[hsl(217,91%,63%)] transition-colors" data-testid="link-login">
              Giriş Yap
            </Link>
          </div>

          <div className="grid grid-cols-1 gap-2 pt-4">
            <Perk icon={ShieldCheck}>Güvenli ve şifrelenmiş bağlantı</Perk>
            <Perk icon={RefreshCw}>20 dakikada otomatik iade garantisi</Perk>
            <Perk icon={Zap}>Anında aktivasyon, ekstra ücret yok</Perk>
          </div>
        </form>
      </main>
    </div>
  );
}

const INPUT_CLS = "w-full bg-[hsl(0,0%,6%)] border border-white/10 focus:border-[hsl(217,91%,60%)] focus:ring-2 focus:ring-[hsl(217,91%,60%)]/30 outline-none pl-11 pr-4 py-3.5 text-sm rounded-2xl transition-colors";

function Field({ label, icon: Icon, hint, children }) {
  return (
    <div>
      <label className="text-sm text-white/80 mb-2 block">{label}</label>
      <div className="relative">
        <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
        {children}
      </div>
      {hint && <div className="text-xs text-white/40 mt-1.5 ml-1">{hint}</div>}
    </div>
  );
}

function ToggleEye({ visible, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors">
      {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
    </button>
  );
}

function Perk({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-3 text-xs text-white/60">
      <span className="w-7 h-7 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center text-[hsl(217,91%,63%)]">
        <Icon className="w-3.5 h-3.5" />
      </span>
      {children}
    </div>
  );
}
